import _, { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { IOwnerData, IProposalPageFormState } from './form';
import ProposalPolicyPersonDrawer from '../../proposal-policy-person-drawer/ProposalPolicyPersonDrawer';
import { useLazyQuery } from '@apollo/client';
import {
  fetchedPersonAddressList,
  fetchedPersonsWithPagination,
} from '../queries';
import { extractPersonsAddresses, extractPersonsFromQuery } from '../utils';
import { useParams } from 'react-router-dom';
import { validatePolicyOwnerField } from '../../page/expat/validation';
import { IProposalDetailsSummary } from './index2';
import PersonAddressDrawer from '@modules/salesforce/persons/person-details-page/person-address-drawer/PersonAddressDrawer';
import WithLinkFormField from '@components/form-fields/WithLinkFormField';
import AutocompleteFormField from '@components/form-fields/AutocompleteFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import { IAbstractRecord } from '@models';
import { MAIN_THEME } from '@styles';

interface IPolicyDetailsWidgetProps {
  pageState: IProposalPageFormState;
  onPageStateUpdate: (pageState: IProposalPageFormState) => void;
  disabledForm?: boolean;
  data: IProposalDetailsSummary;
}

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: `repeat(2, 49%)`,
    gap: '2%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    alignContent: 'ce',
  },
  field: {
    width: '100%',
    marginRight: '25px !important',
    minWidth: '195px',
  },
  orderField: {
    width: '100%',
    marginRight: '25px !important',
    minWidth: '20px',
  },
  sumInsuredRow: {
    width: '100%',
  },
  section: {
    display: 'grid',
    'grid-template-columns': '23.5% 23.5% 23.5% 23.5%',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    '& label': {
      '& span': {
        margin: '0 0 0',
      },
    },
  },
  addBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    padding: '0',
    margin: '20px auto 0',
    display: 'block',
  },
  removeBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    padding: '0',
    margin: '0 auto',
    display: 'block',
  },
  labelHeader: {
    fontSize:`${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '16px !important',
    fontFamily: `${MAIN_THEME.font.medium} !important`,
  },
}));

const PolicyOwnerWidget: React.FC<IPolicyDetailsWidgetProps> = ({
  pageState,
  onPageStateUpdate,
  disabledForm,
  data,
}) => {
  const { classes } = useStyles();
  const params = useParams();
  const entityId = params.id;

  const [getPersonAddressListLazy] = useLazyQuery(fetchedPersonAddressList());

  const [newPersonDrawerOpen, setNewPersonDrawerOpen] =
    useState<boolean>(false);
  const [newAddressDrawerOpen, setNewAddressDrawerOpen] =
    useState<boolean>(false);
  const [currentSection, setCurrentSection] = useState<string | null>(null);

  const [lists, setLists] = useState<
    Record<string, Record<string, Record<string, string> | string>>
  >({
    ownerId: {},
    ownerAddresses: {},
  });

  const values = pageState.values.policyPersons.owner;
  const errors = pageState.errors.policyPersons.owner;
  const touched = pageState.touched.policyPersons.owner;

  const [fetchedPersonsLazy] = useLazyQuery(fetchedPersonsWithPagination());

  const handleOpenDrawer = (section: string) => {
    setCurrentSection(section);
    setNewPersonDrawerOpen(true);
  };

  const handleOpenAddressDrawer = () => {
    setNewAddressDrawerOpen(true);
  };

  const initialize = async () => {
    const newOwnerList: Record<string, string> = {};
    newOwnerList[values.ownerId] = values.fullName;
    const newList = {
      ownerId: { payerPersons: newOwnerList },
    };
    setLists(newList);
  };
  useEffect(() => {
    initialize();
  }, []);

  const handleOwnerAddresses = async (personId: string) => {
    const result = await getPersonAddressListLazy({
      variables: {
        selectedPersonID: personId,
      },
      errorPolicy: 'all',
    });

    return extractPersonsAddresses(result.data);
  };

  const onFieldUpdate = async (
    fieldName: string,
    value: unknown,
    rowIndex?: number,
    touched = false,
    updatedList: typeof lists = lists
  ) => {
    const newPageState = _.cloneDeep(pageState);
    if (fieldName === 'ownerId') {
      (newPageState.values.policyPersons.owner as any)[fieldName] = value;
      newPageState.errors.policyPersons.owner[fieldName] =
        validatePolicyOwnerField(fieldName, value);
      if (touched) {
        newPageState.touched.policyPersons.owner[fieldName] = true;
      }

      if (!isEmpty(value)) {
        const newLists = { ...updatedList };
        const result2 = (await handleOwnerAddresses(value as string)) as Record<
          string,
          Record<string, { address: string; isPrimary: boolean }>
        >;
        const ownerAddresses = result2.ownerPersonAddress;
        const extractedAddresses: IAbstractRecord = {};
        Object.keys(ownerAddresses).forEach((key) => {
          extractedAddresses[key] = ownerAddresses[key].address;
          if (ownerAddresses[key].isPrimary) {
            newPageState.values.policyPersons.owner.ownerAddress = key;
          }
        });
        newPageState.errors.policyPersons.owner.ownerAddress = '';
        newLists['ownerAddresses'] = extractedAddresses;
        setLists(newLists);
      }
    } else {
      (newPageState.values.policyPersons.owner as any)[fieldName] = value;
      newPageState.errors.policyPersons.owner[fieldName] =
        validatePolicyOwnerField(fieldName, value);
      if (touched) {
        newPageState.touched.policyPersons.owner[fieldName] = true;
      }
    }
    onPageStateUpdate(newPageState);
  };

  const handleDrawerSubmitSuccess = (
    person: string,
    newPersonFullName: string
  ) => {
    const newPersonList: Record<string, string> = {};
    newPersonList[person] = newPersonFullName;
    onFieldUpdate('ownerId', [person], 0, false, {
      ownerId: { ownerPersons: newPersonList },
      ownerAddresses: {},
    });
    setNewPersonDrawerOpen(false);
  };

  const handleAddressSubmitSuccess = (
    address: string,
    addressFullName: string
  ) => {
    onFieldUpdate('ownerAddress', address, 0, false, {
      ownerAddresses: { [address]: addressFullName },
    });
    setNewAddressDrawerOpen(false);
  };

  const getDefaultPersonListIfEmpty = (
    list: Record<string, string>,
    owner: IOwnerData
  ) => {
    if (Object.keys(list || {}).length > 0 || !owner.fullName) {
      return list;
    }

    const newList: Record<string, string> = {
      [owner.ownerId]: owner.fullName,
      [owner.ownerAddress]: owner.addressDetails,
    };

    return newList;
  };

  return (
    <div>
      <div className={classes.fieldRow}>
        {newPersonDrawerOpen && currentSection === 'owner' && (
          <ProposalPolicyPersonDrawer
            open={newPersonDrawerOpen}
            onClose={() => setNewPersonDrawerOpen(false)}
            section={currentSection}
            planId={''}
            lineExternalCode={data?.LineId?.ExternalCode}
            onSuccess={() => undefined}
            proposalId={entityId}
            onSubmitSuccess={handleDrawerSubmitSuccess}
          />
        )}

        {newAddressDrawerOpen && (
          <PersonAddressDrawer
            open={newAddressDrawerOpen}
            onClose={() => setNewAddressDrawerOpen(false)}
            personId={values.ownerId}
            onSubmitSuccess={handleAddressSubmitSuccess}
            onSuccess={() => undefined}
          />
        )}
        <WithLinkFormField
          linkText={disabledForm ? '' : 'Add Person'}
          onLinkClick={() => {
            handleOpenDrawer('owner');
          }}
        >
          <AutocompleteFormField
            key="ownerId"
            name="ownerId"
            title="Owner"
            placeholder="Owner"
            value={values.ownerId}
            error={touched.ownerId ? errors.ownerId : ''}
            disabled={disabledForm}
            material
            onChange={(v) => {
              onFieldUpdate('ownerId', v);
            }}
            fetchOptions={function (
              value,
              pagination,
              hasDefaultValue
            ): Promise<{
              options: Record<string, string>;
              totalCount: number;
            }> {
              const defaultValue = hasDefaultValue ? value : null;
              return fetchedPersonsLazy({
                variables: {
                  searchKeyword: hasDefaultValue ? null : value,
                  pagination,
                  selectedPersonID: defaultValue,
                },
              }).then((result) => {
                const options = extractPersonsFromQuery(
                  result.data?.Production?.queries
                    ?.GetAssignedPersonsWithPagination?.items
                );
                return {
                  options,
                  totalCount:
                    result.data?.Production?.queries
                      ?.GetAssignedPersonsWithPagination?.paging?.totalCount,
                };
              });
            }}
          />
        </WithLinkFormField>
        <WithLinkFormField
          linkText={disabledForm ? '' : 'Add Address'}
          onLinkClick={() => {
            handleOpenAddressDrawer();
          }}
        >
          <SelectFormField
            key="ownerAddress"
            name="ownerAddress"
            title="Owner Address"
            placeholder="Owner Address"
            value={values.ownerAddress}
            error={touched.ownerAddress ? errors.ownerAddress : ''}
            material
            disabled={disabledForm}
            onChange={(v) => {
              onFieldUpdate('ownerAddress', v);
            }}
            selectOptions={getDefaultPersonListIfEmpty(
              lists.ownerAddresses as Record<string, string>,
              values
            )}
          />
        </WithLinkFormField>
      </div>
    </div>
  );
};

export default PolicyOwnerWidget;
